import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { AlertTriangle, Camera, Download, Loader2, Package } from "lucide-react";
import { API } from "../../App";

const money = (v) =>
  v == null ? "—" : `€${Number(v).toLocaleString("en-IE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const qty = (v, unit) => `${Number(v || 0).toLocaleString("en-IE")}${unit || ""}`;

/**
 * Stock on hand at cost, ex-VAT.
 *
 * A BALANCE SHEET figure, deliberately separate from the P&L — it is
 * not revenue, not a cost of sale and not an overhead, and it changes
 * no number on the profit statement.
 *
 * `compact` renders just the headline for the top of the Inventory page;
 * the full card (Finance) adds the category breakdown, the month-end
 * chart, "Snapshot now" and the product drill-down.
 */
export const StockValuationCard = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapshotting, setSnapshotting] = useState(false);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, h] = await Promise.all([
        axios.get(`${API}/stock/valuation`),
        compact ? Promise.resolve({ data: { snapshots: [] } })
          : axios.get(`${API}/stock/valuation/history`),
      ]);
      setData(v.data);
      setHistory(h.data.snapshots || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load stock value");
    } finally {
      setLoading(false);
    }
  }, [compact]);

  useEffect(() => { load(); }, [load]);

  const snapshotNow = async () => {
    setSnapshotting(true);
    try {
      const r = await axios.post(`${API}/stock/valuation/snapshot`);
      toast.success(`Snapshot saved for ${r.data.month}: ${money(r.data.total_value)}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Snapshot failed");
    } finally {
      setSnapshotting(false);
    }
  };

  const openDrill = async () => {
    try {
      const r = await axios.get(`${API}/stock/valuation/products`);
      setDrill(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the drill-down");
    }
  };

  const missing = data?.units_without_cost;
  const maxVal = Math.max(...history.map((h) => h.total_value || 0), 1);

  if (loading && !data) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-slate-800/50 border-slate-700" data-testid="stock-valuation-card">
        <CardContent className={compact ? "p-3" : "p-4 space-y-4"}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                <Package className="w-3 h-3" />
                Stock on hand (at cost)
              </p>
              <p className="text-2xl font-mono text-white" data-testid="stock-valuation-total">
                {money(data?.total_value)}
              </p>
              <p className="text-[11px] text-slate-500">
                ex-VAT · {data?.valued_unit_count} unit(s) valued · includes
                technician floats · not part of the P&amp;L
              </p>
            </div>
            {!compact && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={snapshotNow}
                        disabled={snapshotting}
                        className="border-slate-600 text-slate-300 h-8"
                        data-testid="stock-valuation-snapshot-btn">
                  {snapshotting
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <Camera className="w-3 h-3 mr-1" />}
                  Snapshot now
                </Button>
                <Button size="sm" variant="outline" onClick={openDrill}
                        className="border-slate-600 text-slate-300 h-8"
                        data-testid="stock-valuation-drilldown-btn">
                  By product
                </Button>
              </div>
            )}
          </div>

          {missing?.count > 0 && (
            <p className="text-[11px] text-amber-400 flex items-start gap-1.5"
               data-testid="stock-valuation-missing-cost">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              {missing.message} — add their delivery cost to include them.
            </p>
          )}

          {!compact && (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  By category
                </p>
                <div className="space-y-1">
                  {(data?.by_category || []).filter((c) => c.value > 0).map((c) => (
                    <div key={c.category} className="flex justify-between text-xs"
                         data-testid={`stock-valuation-category-${c.category}`}>
                      <span className="text-slate-300">
                        {c.category}
                        <span className="text-slate-500 ml-2">{c.unit_count} unit(s)</span>
                      </span>
                      <span className="font-mono text-white">{money(c.value)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Month-end values{" "}
                  <span className="normal-case">(from snapshots, never recomputed)</span>
                </p>
                {history.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    No snapshots yet — press &ldquo;Snapshot now&rdquo; to take the first.
                  </p>
                ) : (
                  <div className="flex items-end gap-1.5 h-24"
                       data-testid="stock-valuation-chart">
                    {history.map((h) => (
                      <div key={h.month} className="flex-1 flex flex-col items-center gap-1"
                           title={`${h.month}: ${money(h.total_value)}`}>
                        <div
                          className="w-full bg-saturn-orange/70 hover:bg-saturn-orange rounded-t transition-colors"
                          style={{ height: `${Math.max((h.total_value / maxVal) * 72, 2)}px` }}
                        />
                        <span className="text-[9px] text-slate-500 whitespace-nowrap">
                          {h.month.slice(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-5xl max-h-[85vh] overflow-y-auto"
                       data-testid="stock-valuation-drilldown">
          <DialogHeader>
            <DialogTitle>Stock on hand by product</DialogTitle>
            <DialogDescription className="text-slate-400">
              {money(drill?.total_value)} at cost, ex-VAT. Remaining quantity ×
              (unit cost ÷ initial quantity) — the same arithmetic that charges
              film to a job.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button size="sm" variant="outline"
                    className="border-slate-600 text-slate-300 h-8"
                    onClick={() => window.open(`${API}/accounting/export/stock-on-hand.csv`, "_blank")}
                    data-testid="stock-valuation-csv-btn">
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 uppercase text-[10px]">
                  <th className="text-left py-1.5 pr-3">Product</th>
                  <th className="text-left py-1.5 pr-3">Category</th>
                  <th className="text-right py-1.5 pr-3">Units</th>
                  <th className="text-right py-1.5 pr-3">Remaining</th>
                  <th className="text-right py-1.5 pr-3">Unit cost</th>
                  <th className="text-right py-1.5 pr-3">Value</th>
                  <th className="text-left py-1.5">Location</th>
                </tr>
              </thead>
              <tbody>
                {(drill?.by_product || []).map((p, i) => (
                  <React.Fragment key={p.product_id}>
                    <tr className="border-t border-slate-700 bg-slate-800/40"
                        data-testid={`stock-valuation-product-${i}`}>
                      <td className="py-1.5 pr-3 text-white">{p.product_name}</td>
                      <td className="py-1.5 pr-3 text-slate-400">{p.category}</td>
                      <td className="py-1.5 pr-3 text-right text-slate-300">{p.unit_count}</td>
                      <td className="py-1.5 pr-3 text-right text-slate-300">
                        {qty(p.remaining_quantity, p.base_unit)}
                      </td>
                      <td className="py-1.5 pr-3 text-right text-slate-500">—</td>
                      <td className="py-1.5 pr-3 text-right font-mono text-white">
                        {money(p.value)}
                      </td>
                      <td className="py-1.5 text-slate-400">
                        {Object.entries(p.locations || {})
                          .map(([k, v]) => `${k} ${money(v)}`).join(" · ")}
                      </td>
                    </tr>
                    {(p.units || []).map((u) => (
                      <tr key={u.unit_id} className="border-t border-slate-800/60">
                        <td className="py-1 pr-3 pl-4 text-slate-500 text-[11px]">
                          {u.unit_id.slice(-8)}
                        </td>
                        <td />
                        <td />
                        <td className="py-1 pr-3 text-right text-slate-400">
                          {qty(u.remaining_quantity, u.base_unit)}
                        </td>
                        <td className="py-1 pr-3 text-right font-mono text-slate-400">
                          {money(u.unit_cost)}
                        </td>
                        <td className="py-1 pr-3 text-right font-mono text-slate-300">
                          {money(u.value)}
                        </td>
                        <td className="py-1 text-slate-500">
                          {u.location}
                          {u.pulled_by_name ? ` · ${u.pulled_by_name}` : ""}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {drill?.units_without_cost?.count > 0 && (
            <p className="text-[11px] text-amber-400">
              {drill.units_without_cost.message}.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
